import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Delete, User } from "lucide-react";

const BG = "/yaz-wall.webp";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const press = (d) => { if (pin.length < 8) setPin(pin + d); };
  const back = () => setPin(pin.slice(0, -1));

  const submit = async () => {
    if (!username.trim() || !pin) { toast.error("Enter username and PIN"); return; }
    setBusy(true);
    try {
      await login(username.trim(), pin);
      navigate("/admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
      setPin("");
    } finally { setBusy(false); }
  };

  return (
    <div className="h-screen w-full relative flex items-center justify-center overflow-hidden">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-8">
          <h1 className="font-display font-black text-6xl tracking-tight text-white">
            YAZ<span className="text-[#FF007F] neon-glow-pink">.</span>
          </h1>
          <p className="font-display font-bold text-lg tracking-[0.25em] text-[#00F0FF] neon-glow-blue mt-1">
            WORKINGTON
          </p>
          <p className="font-display font-semibold text-sm tracking-[0.35em] text-[#A1A1AA] mt-1">
            MANAGEMENT SYSTEM
          </p>
        </div>

        <div className="bg-[#18181B] border border-white/10 rounded-2xl p-6">
          <label className="flex items-center gap-3 bg-[#09090B] border border-white/10 rounded-xl px-4 h-16 mb-4">
            <User className="w-6 h-6 text-[#A1A1AA]" />
            <input
              data-testid="login-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="USERNAME"
              className="bg-transparent outline-none text-white font-display font-bold text-xl tracking-wide w-full placeholder:text-[#52525B]"
            />
          </label>

          <div data-testid="pin-display" className="flex justify-center gap-3 h-16 items-center mb-4">
            {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
              <div key={i} className={`w-5 h-5 rounded-full ${i < pin.length ? "bg-[#FF007F]" : "bg-white/15"}`} />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <button
                key={n}
                data-testid={`pin-key-${n}`}
                onClick={() => press(String(n))}
                className="h-20 rounded-xl bg-[#27272A] hover:bg-[#3f3f46] active:scale-95 transition-transform duration-75 font-display font-bold text-3xl text-white"
              >{n}</button>
            ))}
            <button data-testid="pin-clear" onClick={() => setPin("")}
              className="h-20 rounded-xl bg-[#27272A] hover:bg-[#3f3f46] active:scale-95 transition-transform duration-75 font-display font-bold text-lg text-[#A1A1AA]">CLR</button>
            <button data-testid="pin-key-0" onClick={() => press("0")}
              className="h-20 rounded-xl bg-[#27272A] hover:bg-[#3f3f46] active:scale-95 transition-transform duration-75 font-display font-bold text-3xl text-white">0</button>
            <button data-testid="pin-backspace" onClick={back}
              className="h-20 rounded-xl bg-[#27272A] hover:bg-[#3f3f46] active:scale-95 transition-transform duration-75 flex items-center justify-center">
              <Delete className="w-8 h-8 text-[#A1A1AA]" />
            </button>
          </div>

          <button
            data-testid="login-submit-button"
            onClick={submit}
            disabled={busy}
            className="mt-5 w-full h-20 rounded-xl bg-[#FF007F] hover:bg-[#ff2b95] active:scale-95 transition-transform duration-75 font-display font-black text-2xl tracking-wide text-white disabled:opacity-50"
          >{busy ? "…" : "SIGN IN"}</button>
        </div>
      </div>
    </div>
  );
}
